import { extractStorageObjectKey } from '../utils/image';

// Developer service with custom API client that bypasses /api prefix

// Create a custom API service for developer endpoints that bypasses the /api prefix
const createDeveloperApiService = () => {
  // Default to production API URL if env not set
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.greenguestbd.com/api';

  const getAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem('authToken');
    const devToken = localStorage.getItem('dev_token');
    const authToken = devToken || token;
    return {
      ...(authToken && { Authorization: `Bearer ${authToken}` }),
    };
  };

  const handleResponse = async <T>(response: Response): Promise<T> => {
    if (!response.ok) {

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('dev_token');
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.dispatchEvent(new CustomEvent('authFailed'));
      }

      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  return {
    async get<T>(endpoint: string): Promise<T> {
      const url = `${API_BASE_URL}${endpoint}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      return handleResponse<T>(response);
    },

    async post<T>(endpoint: string, data?: any, options?: { headers?: HeadersInit }): Promise<T> {
      const url = `${API_BASE_URL}${endpoint}`;

      const isFormData = data instanceof FormData;

      // Build headers - for FormData, don't set Content-Type (browser will set it with boundary automatically)
      const authHeaders = getAuthHeaders();
      const optionHeaders = options?.headers || {};

      // Remove Content-Type from option headers if FormData (browser needs to set boundary)
      const cleanOptionHeaders = isFormData
        ? Object.fromEntries(Object.entries(optionHeaders).filter(([key]) => key.toLowerCase() !== 'content-type'))
        : optionHeaders;

      const headers: HeadersInit = {
        ...authHeaders,
        ...cleanOptionHeaders,
        // Only set Content-Type for non-FormData requests
        ...(isFormData ? {} : (data ? { 'Content-Type': 'application/json' } : {})),
      };

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: isFormData ? data : (data ? JSON.stringify(data) : undefined),
      });

      return handleResponse<T>(response);
    },

    async put<T>(endpoint: string, data?: any): Promise<T> {
      const url = `${API_BASE_URL}${endpoint}`;
      const headers = {
        ...getAuthHeaders(),
        ...(data && { 'Content-Type': 'application/json' })
      };

      const response = await fetch(url, {
        method: 'PUT',
        headers,
        body: data ? JSON.stringify(data) : undefined,
      });

      return handleResponse<T>(response);
    },

    async delete<T>(endpoint: string): Promise<T> {
      const url = `${API_BASE_URL}${endpoint}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      return handleResponse<T>(response);
    }
  };
};

const developerApiService = createDeveloperApiService();

export interface Developer {
  id: string;
  username: string;
  email: string;
  isActive: boolean;
  isSuperAdmin: boolean;
  permissions: string[];
  lastLoginAt?: Date;
  lastLoginIp?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface DeveloperLoginRequest {
  username: string;
  password: string;
}

export interface DeveloperLoginResponse {
  access_token: string;
  developer: Developer;
}

export interface DeveloperStats {
  totalUsers: number;
  totalProducts: number;
  totalOrders: number;
  totalRevenue: number;
  activeUsers: number;
  pendingOrders: number;
  lowStockProducts: number;
  recentActivity: any[];
}

export interface CreateDeveloperRequest {
  username: string;
  email: string;
  password: string;
  isSuperAdmin?: boolean;
  permissions?: string[];
}

export interface UpdateDeveloperRequest {
  username?: string;
  email?: string;
  password?: string;
  isActive?: boolean;
  isSuperAdmin?: boolean;
  permissions?: string[];
}

class DeveloperService {
  private baseUrl = '/dev-secret-admin-panel'; // Developer routes under API_BASE_URL

  async login(credentials: DeveloperLoginRequest): Promise<DeveloperLoginResponse> {
    const response = await developerApiService.post(`${this.baseUrl}/auth/login`, credentials);
    return response as DeveloperLoginResponse;
  }

  async register(developerData: CreateDeveloperRequest): Promise<Developer> {
    const response = await developerApiService.post(`${this.baseUrl}/auth/register`, developerData);
    return response as Developer;
  }

  async initializeFirstDeveloper(): Promise<Developer> {
    const response = await developerApiService.post(`${this.baseUrl}/auth/initialize`);
    return response as Developer;
  }

  async verifyPassword(password: string): Promise<{ verified: boolean; message: string }> {
    const response = await developerApiService.post(`${this.baseUrl}/auth/verify-password`, { password });
    return response as { verified: boolean; message: string };
  }

  async getProfile(): Promise<Developer> {
    const response = await developerApiService.get(`${this.baseUrl}/profile`);
    return response as Developer;
  }

  async getAllDevelopers(): Promise<Developer[]> {
    const response = await developerApiService.get(`${this.baseUrl}/developers`);
    return response as Developer[];
  }

  async getDeveloper(id: string): Promise<Developer> {
    const response = await developerApiService.get(`${this.baseUrl}/developers/${id}`);
    return response as Developer;
  }

  async updateDeveloper(id: string, data: UpdateDeveloperRequest): Promise<Developer> {
    const response = await developerApiService.put(`${this.baseUrl}/developers/${id}`, data);
    return response as Developer;
  }

  async deleteDeveloper(id: string): Promise<void> {
    await developerApiService.delete(`${this.baseUrl}/developers/${id}`);
  }

  async getSystemStats(): Promise<DeveloperStats> {
    const response = await developerApiService.get(`${this.baseUrl}/stats`);
    return response as DeveloperStats;
  }

  async getAllData(): Promise<any> {
    const response = await developerApiService.get(`${this.baseUrl}/all-data`);
    return response;
  }

  async getUsers(): Promise<any> {
    const response = await developerApiService.get(`${this.baseUrl}/users`);
    return response;
  }

  async getProducts(): Promise<any> {
    const timestamp = Date.now();
    const response = await developerApiService.get(`${this.baseUrl}/products?t=${timestamp}`);
    return response;
  }

  async getOrders(): Promise<any> {
    const response = await developerApiService.get(`${this.baseUrl}/orders`);
    return response;
  }

  async getAnalytics(): Promise<any> {
    const response = await developerApiService.get(`${this.baseUrl}/analytics`);
    return response;
  }

  async getInventory(): Promise<any> {
    const response = await developerApiService.get(`${this.baseUrl}/inventory`);
    return response;
  }

  async clearCache(): Promise<any> {
    const response = await developerApiService.post(`${this.baseUrl}/actions/clear-cache`);
    return response;
  }

  async backupData(): Promise<Blob> {
    const url = `${process.env.REACT_APP_API_URL || 'https://api.greenguestbd.com/api'}${this.baseUrl}/actions/backup-data`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.getToken()}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.blob();
  }

  async resetSystem(): Promise<any> {
    const response = await developerApiService.post(`${this.baseUrl}/actions/reset-system`);
    return response;
  }

  async restoreData(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('backupFile', file);

    const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.greenguestbd.com/api'}${this.baseUrl}/actions/restore-data`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.getToken()}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async healthCheck(): Promise<any> {
    const response = await developerApiService.get(`${this.baseUrl}/health`);
    return response;
  }

  async testEndpoint(): Promise<any> {
    const response = await developerApiService.get(`${this.baseUrl}`);
    return response;
  }

  // Edit methods
  async updateUser(id: number, data: any): Promise<any> {
    const response = await developerApiService.put(`${this.baseUrl}/users/${id}`, data);
    return response;
  }

  async updateProduct(id: number, data: any): Promise<any> {
    const response = await developerApiService.put(`${this.baseUrl}/products/${id}`, data);
    return response;
  }

  async updateOrder(id: number, data: any): Promise<any> {
    const response = await developerApiService.put(`${this.baseUrl}/orders/${id}`, data);
    return response;
  }

  async deleteOrder(id: number): Promise<any> {
    const response = await developerApiService.delete(`${this.baseUrl}/orders/${id}`);
    return response;
  }

  async deleteOrderItem(id: number): Promise<any> {
    const response = await developerApiService.delete(`${this.baseUrl}/order-items/${id}`);
    return response;
  }

  async deleteUser(id: number): Promise<any> {
    const response = await developerApiService.delete(`${this.baseUrl}/users/${id}`);
    return response;
  }

  async deleteProduct(id: number): Promise<any> {
    const response = await developerApiService.delete(`${this.baseUrl}/products/${id}`);
    return response;
  }

  async uploadImage(file: File, customFileName?: string): Promise<{ fileUrl: string; fileName: string; message: string }> {
    try {
      const formData = new FormData();
      formData.append('image', file);
      if (customFileName?.trim()) {
        formData.append('customFileName', customFileName.trim());
      }

      const uploadUrl = `${this.baseUrl}/upload/image`;
      const response = await developerApiService.post<{ fileUrl: string; fileName: string; message: string }>(uploadUrl, formData);

      if (!response?.fileUrl) {
        throw new Error('Invalid response from server: missing fileUrl');
      }

      return response;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to upload image';
      throw new Error(errorMessage);
    }
  }

  async deleteImage(urlOrFileName: string): Promise<any> {
    const objectKey = extractStorageObjectKey(urlOrFileName);
    if (!objectKey) {
      throw new Error('Invalid image reference');
    }
    const response = await developerApiService.delete(
      `${this.baseUrl}/upload/image/${encodeURIComponent(objectKey)}`,
    );
    return response;
  }

  // Admin Logs
  async getAdminLogs(filename: string = 'current'): Promise<any> {
    const response = await developerApiService.get(`${this.baseUrl}/logs/admin?file=${filename}`);
    return response;
  }

  async getAdminLogFiles(): Promise<any> {
    const response = await developerApiService.get(`${this.baseUrl}/logs/admin/files`);
    return response;
  }

  async downloadAdminLog(filename: string = 'current'): Promise<Blob> {
    const url = `${process.env.REACT_APP_API_URL || 'https://api.greenguestbd.com/api'}${this.baseUrl}/logs/admin/download?file=${filename}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${this.getToken()}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.blob();
  }

  // Token management
  setToken(token: string): void {
    localStorage.setItem('dev_token', token);
  }

  getToken(): string | null {
    return localStorage.getItem('dev_token');
  }

  removeToken(): void {
    localStorage.removeItem('dev_token');
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    try {
      // Decode JWT token to check expiration
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp > currentTime;
    } catch {
      return false;
    }
  }
}

export const developerService = new DeveloperService();
